'use strict'

import * as React from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { Link, Checkbox, CircularProgress } from '@mui/material'
import CheckBoxRoundedIcon from '@mui/icons-material/CheckBoxRounded'
import { useSelector } from 'react-redux'
import { produce } from 'immer'

import { ChrofyCustomSelect } from '../../common/ChrofyCustomSelect'
import { getMessage } from '../../../common/utils/MessageUtils'
import Forbidden from '../../../_components/Forbidden'
import { fetchDataUpdateTaskInfo } from './extra/services'
import { taskCombinedUpdateWaitCount } from './extra/helpers'
import { titleName } from '../../../_constants/Code'

const DISPLAY_STATUS_LABEL = {
  confirmed: '確定済',
  pending_reupdate: '再更新待ち',
  action_required: '要対応',
  in_progress: '作業中',
  updating: '更新中',
  not_started: '未着手',
  updated: '更新済'
}

const STATUS_BADGE_CLASS = {
  confirmed: 'bg-slate-200 text-slate-600',
  pending_reupdate: 'bg-violet-100 text-violet-800',
  action_required: 'bg-red-100 red--text',
  in_progress: 'bg-amber-100 text-amber-900',
  updating: 'bg-sky-100 text-sky-900',
  not_started: 'bg-gray-200 text-gray-700',
  updated: 'bg-green-100 green--text'
}

const STATUS_BADGE_BASE = 'inline-block rounded-xl px-3 py-1 text-extra-small font-medium'

const PROGRESS_SEGMENT_CLASS = {
  updated: 'bg-green-500',
  update_pending: 'bg-green-400',
  reupdate_pending: 'bg-green-300',
  verifying: 'bg-sky-300',
  verification_pending: 'bg-sky-200',
  error: 'bg-red-300',
  configuring: 'bg-gray-300',
  unset: 'bg-gray-200'
}

const PROGRESS_SEGMENT_LABEL = {
  updated: '更新済',
  update_pending: '更新待ち',
  reupdate_pending: '再更新待ち',
  verification_pending: '検証待ち',
  verifying: '検証中',
  error: '検証エラー',
  configuring: '設定中',
  unset: '未設定'
}

const STATE_IDLE = 'IDLE'
const STATE_LOADING = 'LOADING'
const STATE_ERROR = 'ERROR'

const PROGRESS_PCT_WIDTH_CLASS = 'w-[90px]'

const initialTaskListState = () => ({
  uiState: STATE_LOADING,
  errorMessage: '',
  fiscalPeriodOptions: [],
  fiscalPeriodGroups: [],
  fiscalFilter: '',
  periodKindFilter: { monthly: true, quarter: true, year: true },
  statusFilter: {
    not_started: true,
    in_progress: true,
    updating: true,
    action_required: true,
    updated: true,
    confirmed: true,
    pending_reupdate: true
  },
  accordionExpanded: {}
})

/**
 * @param {ReturnType<typeof initialTaskListState>} state
 * @param {{ type: string; payload?: any }} action
 */
function taskListReducer(state, action) {
  return produce(state, (draft) => {
    switch (action.type) {
      case 'INIT_START':
        draft.uiState = STATE_LOADING
        draft.errorMessage = ''
        break
      case 'INIT_SUCCESS':
        draft.fiscalPeriodOptions = action.payload.fiscalPeriodOptions
        draft.fiscalPeriodGroups = action.payload.fiscalPeriodGroups
        draft.uiState = STATE_IDLE
        break
      case 'INIT_ERROR':
        draft.errorMessage = action.payload.message
        draft.uiState = STATE_ERROR
        break
      case 'SET_FISCAL_FILTER':
        draft.fiscalFilter = action.payload
        break
      case 'SET_PERIOD_KIND':
        draft.periodKindFilter[action.payload.key] = action.payload.checked
        break
      case 'SET_STATUS_FILTER':
        draft.statusFilter[action.payload.key] = action.payload.checked
        break
      case 'TOGGLE_ACCORDION':
        draft.accordionExpanded[action.payload] = !draft.accordionExpanded[action.payload]
        break
      case 'EXPAND_ALL': {
        const next = {}
        action.payload.forEach((fp) => {
          next[fp] = true
        })
        draft.accordionExpanded = next
        break
      }
      case 'COLLAPSE_ALL':
        draft.accordionExpanded = {}
        break
      default:
        break
    }
  })
}

/**
 * 人事/財務の更新状況プログレスバー
 *
 * @param {{ status: object }} props
 */
const SegmentProgressBar = ({ status }) => {
  if (!status || status.total === 0) {
    return (
      <div className="flex w-full min-w-0 items-center gap-2">
        <div className="flex h-2.5 min-w-0 flex-1 overflow-hidden rounded bg-slate-100" />
        <span className={`text-caption-2 shrink-0 text-right ${PROGRESS_PCT_WIDTH_CLASS}`}>0% (0/0)</span>
      </div>
    )
  }
  const updated = status.updated ?? 0
  const updatePending = status.updatePending ?? 0
  const reupdatePending = status.reupdatePending ?? 0
  const verificationPending = status.verificationPending ?? 0
  const verifying = status.verifying ?? 0
  const error = status.error ?? 0
  const configuring = status.configuring ?? 0
  const unset = status.unset ?? 0
  const { total } = status
  const segments = [
    { key: 'updated', value: updated },
    { key: 'update_pending', value: updatePending },
    { key: 'reupdate_pending', value: reupdatePending },
    { key: 'verifying', value: verifying },
    { key: 'verification_pending', value: verificationPending },
    { key: 'error', value: error },
    { key: 'configuring', value: configuring },
    { key: 'unset', value: unset }
  ].filter((s) => s.value > 0)
  const displayUpdated = total > 0 ? Math.min(updated, total) : 0
  const pct = total > 0 ? Math.min(100, Math.round((displayUpdated / total) * 100)) : 0
  return (
    <div className="flex w-full min-w-0 items-center gap-2">
      <div className="flex h-2.5 min-w-0 flex-1 overflow-hidden rounded bg-slate-100">
        {segments.map((s) => (
          <div
            key={s.key}
            className={`h-full min-w-[4px] ${PROGRESS_SEGMENT_CLASS[s.key] || 'bg-gray-200'}`}
            style={{
              width: 'var(--tasklist-segment-pct)',
              '--tasklist-segment-pct': `${(s.value / total) * 100}%`
            }}
            title={`${PROGRESS_SEGMENT_LABEL[s.key] || s.key}: ${s.value}件`}
          />
        ))}
      </div>
      <span className={`text-caption-2 shrink-0 text-right ${PROGRESS_PCT_WIDTH_CLASS}`}>
        {pct}% ({displayUpdated}/{total})
      </span>
    </div>
  )
}

/** @param {{ task: object }} props */
const TaskActionCell = ({ task }) => {
  const err = (task.personnelUpdateStatus?.error ?? 0) + (task.accountingUpdateStatus?.error ?? 0)
  const wait = taskCombinedUpdateWaitCount(task)
  if (err === 0 && wait === 0) return '-'
  return (
    <span className="flex flex-wrap items-center gap-2">
      {wait > 0 && (
        <span
          title={`更新待ち・再更新待ちが ${wait}件 あります`}
          className="matrix-legend-chip matrix-legend-chip--bg-set inline-flex items-center gap-1 font-semibold"
        >
          <span className="text-sm">⏳</span>
          {`更新待 ${wait}件`}
        </span>
      )}
      {err > 0 && (
        <span
          title={`検証エラーが ${err}件 あります`}
          className="matrix-legend-chip matrix-legend-chip--bg-required inline-flex items-center gap-1 font-semibold red--text"
        >
          <span className="text-sm">⚠</span>
          {`エラー ${err}件`}
        </span>
      )}
    </span>
  )
}

const PERIOD_BLOCK_DEFS = [
  { kind: 'monthly', title: '月次データ更新タスク' },
  { kind: 'quarter', title: '四半期データ更新タスク' },
  { kind: 'year', title: '年次データ更新タスク' }
]

/**
 * 凡例（プログレス色の見方）
 *
 * @param {object} props
 */
const TaskListProgressLegend = () => (
  <div className="mt-6 flex flex-col gap-2.5 rounded-lg border border-grey bg-white p-3 px-5">
    <p className="text-caption-2 m-0 leading-relaxed text-slate-500">
      「人事データ更新状況」「財務データ更新状況」はそれぞれのデータ分類ごとの取り込み状況を表します。
    </p>
    <div className="flex flex-wrap items-center gap-x-1.5 gap-y-2">
      <span className="shrink-0 text-caption-2 font-semibold text-slate-600">色の見方：</span>
      {[
        { key: 'updated', label: '更新済' },
        { key: 'update_pending', label: '更新待ち' },
        { key: 'reupdate_pending', label: '再更新待ち' },
        { key: 'verifying', label: '検証中' },
        { key: 'verification_pending', label: '検証待ち' },
        { key: 'error', label: '検証エラー' },
        { key: 'configuring', label: '設定中' },
        { key: 'unset', label: '未設定' }
      ].map(({ key, label }, idx, arr) => (
        <React.Fragment key={label}>
          <span className="inline-flex items-center gap-1.5 text-caption-2 text-slate-600">
            <span
              className={`inline-block h-2.5 w-3.5 shrink-0 rounded-sm ${PROGRESS_SEGMENT_CLASS[key] || 'bg-gray-200'}`}
            />
            {label}
          </span>
          {idx < arr.length - 1 && (
            <span className="mx-0.5 text-caption-2 text-slate-500" aria-hidden>
              |
            </span>
          )}
        </React.Fragment>
      ))}
    </div>
  </div>
)

/**
 * フィルター帯（決算期・期間・ステータス）
 *
 * @param {object} props
 */
const TaskListFilterBar = ({
  fiscalFilter,
  fiscalPeriodSelectOptions,
  periodKindFilter,
  statusFilter,
  onFiscalChange,
  onPeriodKindChange,
  onStatusFilterChange
}) => (
  <div className="card mb-5 px-8 py-4">
    <div className="flex flex-nowrap items-center gap-x-5 overflow-x-auto">
      <div className="flex shrink-0 items-center gap-2.5">
        <label className="whitespace-nowrap text-caption-2 font-semibold grey--text" htmlFor="task-list-fiscal-filter">
          決算期：
        </label>
        <ChrofyCustomSelect
          hasNoSelect={false}
          id="task-list-fiscal-filter"
          name="決算期"
          className="min-w-[180px] max-w-xs shrink-0"
          value={fiscalFilter}
          onChange={(e) => onFiscalChange(e.target.value)}
          options={fiscalPeriodSelectOptions}
          lightNoSelect={false}
        />
      </div>
      <span className="grey lighten-35 h-7 w-px shrink-0" aria-hidden />
      <div className="flex shrink-0 items-center gap-x-[14px]">
        <span className="mr-0.5 whitespace-nowrap text-caption-2 font-semibold grey--text">期間：</span>
        {['monthly', 'quarter', 'year'].map((kind) => (
          <label
            key={kind}
            className="inline-flex cursor-pointer items-center gap-0 whitespace-nowrap rounded-md py-0.5 pl-0.5 pr-1 text-caption-2 grey--text"
          >
            <Checkbox
              size="small"
              checkedIcon={<CheckBoxRoundedIcon />}
              checked={periodKindFilter[kind]}
              className="p-0.5"
              onChange={(e) => onPeriodKindChange(kind, e.target.checked)}
            />
            {kind === 'monthly' ? '月次' : kind === 'quarter' ? '四半期' : '年次'}
          </label>
        ))}
      </div>
      <span className="grey lighten-35 h-7 w-px shrink-0" aria-hidden />
      <div className="flex shrink-0 items-center gap-x-3">
        <span className="mr-0.5 whitespace-nowrap text-caption-2 font-semibold grey--text">ステータス：</span>
        {Object.entries(DISPLAY_STATUS_LABEL).map(([key, label]) => (
          <label
            key={key}
            className="inline-flex cursor-pointer items-center gap-0 whitespace-nowrap rounded-md py-0.5 pl-0.5 pr-1 text-caption-2 grey--text"
          >
            <Checkbox
              size="small"
              checkedIcon={<CheckBoxRoundedIcon />}
              checked={!!statusFilter[key]}
              className="p-0.5"
              onChange={(e) => onStatusFilterChange(key, e.target.checked)}
            />
            {label}
          </label>
        ))}
      </div>
    </div>
  </div>
)

/**
 * 1 決算期分のアコーディオン＋タスク表
 *
 * @param {object} props
 */
const TaskListFiscalAccordion = ({ group, expanded, onToggle, getStatusLabel, getStatusBadgeClassName }) => {
  const errorCount = (group.tasks || []).filter(
    (t) => (t.personnelUpdateStatus?.error ?? 0) + (t.accountingUpdateStatus?.error ?? 0) > 0
  ).length
  const waitingCount = (group.tasks || []).filter((t) => taskCombinedUpdateWaitCount(t) > 0).length
  // タスクステータスが再更新待ち（過去期間の再更新による波及）の件数。
  // データ分類別の集計（waitingCount）ではゼロになるためタスクの displayStatus で数える。
  const reupdateWaitingCount = (group.tasks || []).filter((t) => t.displayStatus === 'pending_reupdate').length
  const hasStatus = errorCount > 0 || waitingCount > 0 || reupdateWaitingCount > 0
  const statusParts = []
  if (errorCount > 0) statusParts.push(`検証エラー ${errorCount}件`)
  if (waitingCount > 0) statusParts.push(`更新待ち ${waitingCount}件`)
  if (reupdateWaitingCount > 0) statusParts.push(`再更新待ち ${reupdateWaitingCount}件`)
  const statusText = statusParts.join('、')

  return (
    <div className="mb-2 overflow-hidden rounded-xl border border-grey-lighten-10 bg-white shadow-sm">
      <div
        role="button"
        tabIndex={0}
        onClick={() => onToggle(group.fiscalPeriod)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onToggle(group.fiscalPeriod)
          }
        }}
        className="flex cursor-pointer select-none items-center bg-[rgba(156,157,151,0.06)] px-6 py-5"
      >
        <span
          className={`mr-4 inline-block w-5 shrink-0 text-center text-body grey--text transition-transform ${expanded ? 'rotate-90' : ''}`}
          aria-hidden
        >
          ▶
        </span>
        <span className="text-lg font-bold grey--text">{group.fiscalPeriod}</span>
        {hasStatus && <span className="ml-4 text-caption-2 font-medium text-slate-500">{statusText}</span>}
      </div>
      <div className={expanded ? 'block px-6 pb-6 pt-5' : 'hidden'}>
        {PERIOD_BLOCK_DEFS.map(({ kind, title }) => {
          const blockTasks = group.tasks.filter((t) => t.periodKind === kind)
          if (blockTasks.length === 0) return null
          return (
            <div key={kind} className={kind !== 'year' ? 'mb-6' : ''}>
              <h3 className="text-h5 mb-2 border-l-4 border-blue1 pl-2.5 font-bold grey--text">{title}</h3>
              <table className="base-table report-list-table w-full text-caption-2 [&_thead_th]:!border-x-0 [&_thead_th]:!py-2.5 [&_thead_th]:!text-left [&_tbody_td]:!border-x-0 [&_tbody_td]:!py-2.5 [&_tbody]:!border-x-0">
                <thead className="base-table-header text-h7">
                  <tr>
                    <th className="min-w-[150px]">対象期間</th>
                    <th className="min-w-[110px]">ステータス</th>
                    <th className="max-w-[480px] min-w-[480px]">人事データ 更新状況</th>
                    <th className="max-w-[250px] min-w-[250px]">財務データ 更新状況</th>
                    <th className="max-w-[100px] min-w-[100px]">要対応</th>
                  </tr>
                </thead>
                <tbody>
                  {blockTasks.map((task) => (
                    <tr key={task.key}>
                      <td className="min-w-[140px]">
                        <Link
                          variant="button"
                          underline="none"
                          component={RouterLink}
                          to={`/data/analysisData/${task.key}`}
                        >
                          {task.periodValue}
                        </Link>
                      </td>
                      <td className="min-w-[120px]">
                        <span className={getStatusBadgeClassName(task.displayStatus)}>
                          {getStatusLabel(task.displayStatus)}
                        </span>
                      </td>
                      <td className="min-w-[240px]">
                        <SegmentProgressBar status={task.personnelUpdateStatus} />
                      </td>
                      <td className="min-w-[180px]">
                        <SegmentProgressBar status={task.accountingUpdateStatus} />
                      </td>
                      <td className="max-w-[160px] min-w-[100px]">
                        <TaskActionCell task={task} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        })}
      </div>
    </div>
  )
}

/**
 * データ更新タスク一覧（IF02098）のメインコンテンツ
 *
 * @param {{ corporationNumber: string }} props
 */
const TaskListV2Content = ({ corporationNumber }) => {
  const [state, dispatch] = React.useReducer(taskListReducer, undefined, initialTaskListState)
  const corporationNumberRef = React.useRef(corporationNumber)
  corporationNumberRef.current = corporationNumber

  React.useEffect(() => {
    document.title = titleName.taskList + titleName.common
  }, [])

  const fiscalPeriodSelectOptions = React.useMemo(
    () => [{ label: '全期間', value: '' }, ...state.fiscalPeriodOptions.map((opt) => ({ label: opt, value: opt }))],
    [state.fiscalPeriodOptions]
  )

  // 初期データ取得はマウント時のみ（component.mdc §3）。再試行時の法人番号は corporationNumberRef / runInit を使用。
  React.useEffect(() => {
    let cancelled = false
    dispatch({ type: 'INIT_START' })
    ;(async () => {
      try {
        const data = await fetchDataUpdateTaskInfo(corporationNumberRef.current)
        if (cancelled) return
        dispatch({
          type: 'INIT_SUCCESS',
          payload: {
            fiscalPeriodOptions: data.fiscalPeriodOptions,
            fiscalPeriodGroups: data.fiscalPeriodGroups
          }
        })
      } catch (err) {
        if (cancelled) return
        const msg = err?.message?.trim()
        dispatch({
          type: 'INIT_ERROR',
          payload: {
            message: msg || getMessage('E011004', 'データ更新タスク情報の取得')
          }
        })
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const filteredGroups = React.useMemo(() => {
    let groups = state.fiscalPeriodGroups
    if (state.fiscalFilter) {
      groups = groups.filter((g) => g.fiscalPeriod === state.fiscalFilter)
    }
    const mapped = groups.map((g) => ({
      ...g,
      tasks: g.tasks.filter((task) => {
        const kindOk =
          (state.periodKindFilter.monthly && task.periodKind === 'monthly') ||
          (state.periodKindFilter.quarter && task.periodKind === 'quarter') ||
          (state.periodKindFilter.year && task.periodKind === 'year')
        if (!kindOk) return false
        const statusKey = task.displayStatus
        return !!state.statusFilter[statusKey]
      })
    }))
    const yearFromFiscal = (fp) => parseInt(String(fp).trim(), 10) || 0
    return mapped.sort((a, b) => yearFromFiscal(a.fiscalPeriod) - yearFromFiscal(b.fiscalPeriod))
  }, [state.fiscalPeriodGroups, state.fiscalFilter, state.periodKindFilter, state.statusFilter])

  /** 表示中の決算期アコーディオンをすべて展開する */
  const handleExpandAll = () => {
    dispatch({ type: 'EXPAND_ALL', payload: filteredGroups.map((g) => g.fiscalPeriod) })
  }

  /** すべての決算期アコーディオンを折りたたむ */
  const handleCollapseAll = () => {
    dispatch({ type: 'COLLAPSE_ALL' })
  }

  const getStatusLabel = (displayStatus) => DISPLAY_STATUS_LABEL[displayStatus] || displayStatus || '-'

  const getStatusBadgeClassName = (displayStatus) =>
    `${STATUS_BADGE_BASE} ${STATUS_BADGE_CLASS[displayStatus] || STATUS_BADGE_CLASS.not_started}`

  /** エラー時の再試行およびデータの再取得（最新の法人番号を ref から参照） */
  const runInit = React.useCallback(() => {
    dispatch({ type: 'INIT_START' })
    ;(async () => {
      try {
        const data = await fetchDataUpdateTaskInfo(corporationNumberRef.current)
        dispatch({
          type: 'INIT_SUCCESS',
          payload: {
            fiscalPeriodOptions: data.fiscalPeriodOptions,
            fiscalPeriodGroups: data.fiscalPeriodGroups
          }
        })
      } catch (err) {
        const msg = err?.message?.trim()
        dispatch({
          type: 'INIT_ERROR',
          payload: {
            message: msg || getMessage('E011004', 'データ更新タスク情報の取得')
          }
        })
      }
    })()
  }, [])

  if (state.uiState === STATE_LOADING) {
    return (
      <div id="wrapper">
        <main className="page-container">
          <section className="page-content">
            <div className="centered-container flex items-center justify-center py-16">
              <CircularProgress />
            </div>
          </section>
        </main>
      </div>
    )
  }

  if (state.uiState === STATE_ERROR) {
    return (
      <div id="wrapper">
        <main className="page-container">
          <section className="page-content">
            <div className="centered-container py-16 text-center">
              <p className="text-body red--text mb-4">{state.errorMessage}</p>
              <button type="button" className="btn primary" onClick={runInit}>
                再試行
              </button>
            </div>
          </section>
        </main>
      </div>
    )
  }

  return (
    <React.Fragment>
      <div id="wrapper">
        <main className="page-container">
          <section className="page-content">
            <div className="centered-container">
              <header className="page-header flex justify-center py-1 pt-8">
                <h1 className="page-header__name text-h1 grey--text py-8">データ更新</h1>
              </header>
              <div id="monthSelectDescription" className="my-5 text-center">
                <p className="text-body grey--text mb-2">
                  データファイルのアップロード・マッピングおよびレポートデータの更新を行うメニューです。
                </p>
                <p className="text-body grey--text m-0">
                  権限(データアップロード・更新／確定・確定解除)が付与されたユーザーのみ利用可能です。
                </p>
              </div>
              <div className="page-body">
                <div className="box-xy-bordered mt-8 w-full max-w-none rounded-t-3xl rounded-b-2xl border-blue1 px-16 pb-16 pt-10">
                  <h2 className="box-bordered-heading blue1 white--text text-h3 -mx-16 -mt-10 rounded-t-2xl px-14 pb-3 pt-2.5 font-medium">
                    データ更新タスク一覧
                  </h2>
                  <div className="container w-full max-w-none">
                    <div className="my-5">
                      <p className="text-body grey--text m-0">
                        対象期間を選択して、データファイルのアップロードとデータマッピングを行ってください。その後、データ更新を行ってください。
                      </p>
                    </div>
                    <TaskListFilterBar
                      fiscalFilter={state.fiscalFilter}
                      fiscalPeriodSelectOptions={fiscalPeriodSelectOptions}
                      periodKindFilter={state.periodKindFilter}
                      statusFilter={state.statusFilter}
                      onFiscalChange={(v) => dispatch({ type: 'SET_FISCAL_FILTER', payload: v })}
                      onPeriodKindChange={(key, checked) =>
                        dispatch({ type: 'SET_PERIOD_KIND', payload: { key, checked } })
                      }
                      onStatusFilterChange={(key, checked) =>
                        dispatch({ type: 'SET_STATUS_FILTER', payload: { key, checked } })
                      }
                    />
                    <div className="mb-2 text-right text-caption-2">
                      <button
                        type="button"
                        className="inline cursor-pointer border-0 bg-transparent p-0 align-baseline text-caption-2 blue1--text underline-offset-2 hover:bg-transparent hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#3150A5]"
                        onClick={handleExpandAll}
                      >
                        すべて展開
                      </button>
                      <span className="mx-1.5 text-caption-2 text-slate-300" aria-hidden>
                        |
                      </span>
                      <button
                        type="button"
                        className="inline cursor-pointer border-0 bg-transparent p-0 align-baseline text-caption-2 blue1--text underline-offset-2 hover:bg-transparent hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#3150A5]"
                        onClick={handleCollapseAll}
                      >
                        すべて折りたたむ
                      </button>
                    </div>
                    <div>
                      {filteredGroups.map((group) => (
                        <TaskListFiscalAccordion
                          key={group.fiscalPeriod}
                          group={group}
                          expanded={!!state.accordionExpanded[group.fiscalPeriod]}
                          onToggle={(fp) => dispatch({ type: 'TOGGLE_ACCORDION', payload: fp })}
                          getStatusLabel={getStatusLabel}
                          getStatusBadgeClassName={getStatusBadgeClassName}
                        />
                      ))}
                    </div>
                    <TaskListProgressLegend />
                  </div>
                </div>
              </div>
            </div>
          </section>
        </main>
      </div>
    </React.Fragment>
  )
}

const TaskListV2 = () => {
  const state = useSelector((s) => s)
  if (!state.authority?.Upload_AuthInfo?.Availability || !state.authority?.Upload_AuthInfo?.Auth_Conf?.View) {
    return <Forbidden />
  }
  return <TaskListV2Content corporationNumber={state.company?.corporationNumber ?? ''} />
}

export default TaskListV2
export { TaskListV2Content }
